import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';

const SettingsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [sessions, setSessions] = useState<Array<{
    id: string;
    createdAt: string;
    lastActivity: string;
    ipAddress: string;
    userAgent: string;
    isCurrentSession: boolean;
  }>>([]);
  const [preferences, setPreferences] = useState({
    notifications: {
      transactions: true,
      profiles: true,
      system: false,
      email: true,
      sms: false
    },
    display: {
      theme: 'light' as 'light' | 'dark' | 'auto',
      language: 'en',
      timezone: 'Asia/Kuala_Lumpur'
    },
    security: {
      twoFactorEnabled: false,
      sessionTimeout: 3600
    }
  });

  const [profileData, setProfileData] = useState({
    fullName: user?.fullName || '',
    username: user?.username || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    loadUserPreferences();
    loadUserSessions();
  }, []);

  const loadUserPreferences = async () => {
    try {
      const response = await userService.getUserPreferences();
      if (response.success && response.data) {
        setPreferences(response.data);
      }
    } catch (error) {
      // Use default preferences if loading fails
      console.error('Failed to load user preferences:', error);
    }
  };

  const loadUserSessions = async () => {
    try {
      const response = await userService.getUserSessions();
      if (response.success && response.data) {
        setSessions(response.data);
      }
    } catch (error) {
      console.error('Failed to load user sessions:', error);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (profileData.newPassword && profileData.newPassword !== profileData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      // Validate the data
      const validationErrors = userService.validateUserData({
        username: profileData.username,
        fullName: profileData.fullName,
        password: profileData.newPassword || undefined
      });

      if (validationErrors.length > 0) {
        setError(validationErrors.join(', '));
        return;
      }

      // Update profile
      const updateData: any = {
        fullName: profileData.fullName,
        username: profileData.username
      };

      if (profileData.newPassword) {
        updateData.currentPassword = profileData.currentPassword;
        updateData.newPassword = profileData.newPassword;
      }

      const response = await userService.updateProfile(updateData);

      if (response.success) {
        setSuccess('Profile updated successfully');
        setProfileData({
          ...profileData,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setError(response.message || 'Failed to update profile');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleViewDocumentation = () => {
    alert("Please ask admin for API access.")
  };

  const handleEnableTwoFactor = async () => {
    try {
      setError('');
      const response = await userService.enableTwoFactor();
      if (response.success && response.data) {
        // Show QR code for setup (would typically open a modal)
        setSuccess('Two-factor authentication setup initiated. Please scan the QR code with your authenticator app.');
      } else {
        setError(response.message || 'Failed to enable two-factor authentication');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to enable two-factor authentication');
    }
  };

  const handleViewSessions = () => {
    setActiveTab('security');
    loadUserSessions();
  };



  const handleUpdatePreferences = async (newPreferences: typeof preferences) => {
    try {
      setError('');
      const response = await userService.updateUserPreferences(newPreferences);
      if (response.success) {
        setPreferences(newPreferences);
        setSuccess('Preferences updated successfully');
      } else {
        setError(response.message || 'Failed to update preferences');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to update preferences');
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: '👤' },
    { id: 'security', name: 'Security', icon: '🔒' },
    { id: 'notifications', name: 'Notifications', icon: '🔔' },
    { id: 'api', name: 'API Settings', icon: '🔑' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-600 mt-2">
              Manage your account settings and preferences
            </p>
          </div>

          <div className="bg-white shadow rounded-lg">
            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 px-6">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="mr-2">{tab.icon}</span>
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
                  {success}
                </div>
              )}

              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Profile Information</h3>
                  <form onSubmit={handleProfileUpdate} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={profileData.fullName}
                          onChange={(e) => setProfileData({...profileData, fullName: e.target.value})}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Username
                        </label>
                        <input
                          type="text"
                          value={profileData.username}
                          onChange={(e) => setProfileData({...profileData, username: e.target.value})}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>
                    </div>

                    <div className="border-t pt-4 mt-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4">Change Password</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Current Password
                          </label>
                          <input
                            type="password"
                            value={profileData.currentPassword}
                            onChange={(e) => setProfileData({...profileData, currentPassword: e.target.value})}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              New Password
                            </label>
                            <input
                              type="password"
                              value={profileData.newPassword}
                              onChange={(e) => setProfileData({...profileData, newPassword: e.target.value})}
                              className="w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Confirm New Password
                            </label>
                            <input
                              type="password"
                              value={profileData.confirmPassword}
                              onChange={(e) => setProfileData({...profileData, confirmPassword: e.target.value})}
                              className="w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md font-medium"
                      >
                        {isLoading ? 'Updating...' : 'Update Profile'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Security Settings</h3>
                  <div className="space-y-6">
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-900">Two-Factor Authentication</h4>
                        <p className="text-sm text-gray-500">Add an extra layer of security to your account</p>
                      </div>
                      <button
                        onClick={handleEnableTwoFactor}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm"
                      >
                        {preferences.security.twoFactorEnabled ? 'Disable' : 'Enable'}
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-900">Login Sessions</h4>
                        <p className="text-sm text-gray-500">Manage your active login sessions</p>
                      </div>
                      <button
                        onClick={handleViewSessions}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm"
                      >
                        View Sessions ({sessions.length})
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50">
                      <div>
                        <h4 className="font-medium text-red-900">Sign Out</h4>
                        <p className="text-sm text-red-700">Sign out from your account</p>
                      </div>
                      <button 
                        onClick={handleLogout}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Preferences</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">Transaction Notifications</h4>
                        <p className="text-sm text-gray-500">Get notified about transaction updates</p>
                      </div>
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600"
                        checked={preferences.notifications.transactions}
                        onChange={(e) => handleUpdatePreferences({
                          ...preferences,
                          notifications: { ...preferences.notifications, transactions: e.target.checked }
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">Profile Updates</h4>
                        <p className="text-sm text-gray-500">Get notified about profile changes</p>
                      </div>
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600"
                        checked={preferences.notifications.profiles}
                        onChange={(e) => handleUpdatePreferences({
                          ...preferences,
                          notifications: { ...preferences.notifications, profiles: e.target.checked }
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">System Maintenance</h4>
                        <p className="text-sm text-gray-500">Get notified about system updates</p>
                      </div>
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600"
                        checked={preferences.notifications.system}
                        onChange={(e) => handleUpdatePreferences({
                          ...preferences,
                          notifications: { ...preferences.notifications, system: e.target.checked }
                        })}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* API Tab */}
              {activeTab === 'api' && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">API Settings</h3>
                  <div className="space-y-4">
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">API Documentation</h4>
                      <p className="text-sm text-gray-500 mb-3">
                        Access the API documentation to integrate with your applications
                      </p>
                      <button onClick={handleViewDocumentation} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm">
                        View Documentation
                      </button>
                    </div>
                    
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">Rate Limits</h4>
                      <p className="text-sm text-gray-500">
                        Current rate limit: 1000 requests per hour
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
